import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import OutsideClick from '../../../functions/OutsideClick';
import { readPreference, usePopoverPlacement, writePreference } from '../../../functions/popover';
import { firstUndrawableVersion, loadEmojiGrid, loadEmojiWords } from '../../../emojis/emojiData';

import "./styles.scss"

const GROUPS = {
    recent: { icon: '🕘', name: 'Недавние' },
    smileys: { icon: '😀', name: 'Смайлики и эмоции' },
    people: { icon: '👋', name: 'Люди и тело' },
    nature: { icon: '🐻', name: 'Животные и природа' },
    food: { icon: '🍔', name: 'Еда и напитки' },
    travel: { icon: '🚗', name: 'Путешествия и места' },
    activities: { icon: '⚽', name: 'Занятия' },
    objects: { icon: '💡', name: 'Предметы' },
    symbols: { icon: '🔣', name: 'Символы' },
    flags: { icon: '🏁', name: 'Флаги' },
};
const SKIN_TONES = ['✋', '✋🏻', '✋🏼', '✋🏽', '✋🏾', '✋🏿'];
const MAX_RECENT = 32;
const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 420;

const COLUMNS = 9;
const ROW_HEIGHT = 36;

const normalize = (text) => text.toLowerCase().replace(/ё/g, 'е');

// A group of the picker's grid. Its emojis are only made once it comes near the visible part of the list:
// making all of them at once, nearly two thousand, slowed the picker's opening. Until then a box of the same
// height keeps the list as long, so the scrollbar and the group tabs work as before.
function EmojiSection({ section, list, eager, renderEmoji }) {
    const ref = useRef(null);
    const [shown, setShown] = useState(eager);

    useEffect(() => {
        if (shown) return undefined;
        if (!window.IntersectionObserver) {
            setShown(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => { if (entries.some((entry) => entry.isIntersecting)) setShown(true); },
            { root: list.current, rootMargin: '300px 0px' },
        );
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [shown, list]);

    return (
        <div className="emoji-group" data-group={section.key} ref={ref}>
            <div className="emoji-group-header">{section.name}</div>
            {section.emojis.length === 0 && <div className="emoji-empty">{section.empty}</div>}
            {shown
                ? <div className="emoji-group-emojis">{section.emojis.map(renderEmoji)}</div>
                : <div style={{ height: Math.ceil(section.emojis.length / COLUMNS) * ROW_HEIGHT }} />}
        </div>
    );
}

function EmojiSelect({ addEmoji }) {
    const ref = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [groups, setGroups] = useState(null);
    const [names, setNames] = useState(null);
    const [query, setQuery] = useState('');
    const [activeGroup, setActiveGroup] = useState(null);
    const [preview, setPreview] = useState(null);
    const [tone, setTone] = useState(() => readPreference('emojiSkinTone', 0));
    const [recent, setRecent] = useState(() => readPreference('recentEmojis', []));
    const { above, alignLeft } = usePopoverPlacement(ref, isOpen, PICKER_WIDTH, PICKER_HEIGHT);

    useEffect(() => {
        let cancelled = false;
        loadEmojiGrid().then(groups => {
            if (!cancelled) setGroups(groups);
        }).catch(err => console.log(err));

        return () => {
            cancelled = true;
        };
    }, []);

    // The names and search words come once the picker is opened.
    useEffect(() => {
        if (!isOpen || names) return undefined;
        let cancelled = false;
        loadEmojiWords().then(names => {
            if (!cancelled) setNames(names);
        }).catch(err => console.log(err));

        return () => {
            cancelled = true;
        };
    }, [isOpen, names]);

    useEffect(() => {
        if (isOpen) searchRef.current?.focus();
    }, [isOpen, groups]);

    OutsideClick(ref, () => setIsOpen(false));

    // Drawable emojis by group, each as { emoji, skins }, plus a lookup by emoji for the recent ones.
    const { shown, byEmoji } = useMemo(() => {
        if (!groups || !isOpen) return { shown: null, byEmoji: null };
        const below = firstUndrawableVersion(groups);
        const byEmoji = new Map();
        const shown = groups.map(group => ({
            key: group.key,
            emojis: group.emojis
                .filter(([, version]) => version < below)
                .map(([emoji, , skins]) => {
                    const entry = { emoji, skins };
                    byEmoji.set(emoji, entry);
                    return entry;
                }),
        }));
        return { shown, byEmoji };
    }, [groups, isOpen]);

    // Each emoji's name, and the words it's found by, once they're in.
    const words = useMemo(() => names && new Map(Object.entries(names).map(
        ([emoji, [name, terms]]) => [emoji, { name, terms: normalize(`${name} ${terms}`) }],
    )), [names]);

    const sections = useMemo(() => {
        if (!shown) return [];
        const terms = normalize(query).split(/\s+/).filter(Boolean);
        if (terms.length > 0) {
            const found = words
                ? shown.flatMap(group => group.emojis).filter(entry => terms.every(term => words.get(entry.emoji)?.terms.includes(term)))
                : [];
            return [{ key: 'search', name: 'Результаты поиска', emojis: found, empty: words ? 'Ничего не найдено' : 'Загрузка…' }];
        }
        const recentEntries = recent.map(emoji => byEmoji.get(emoji)).filter(Boolean);
        return [
            ...(recentEntries.length > 0 ? [{ key: 'recent', emojis: recentEntries }] : []),
            ...shown,
        ].map(section => ({ ...section, name: GROUPS[section.key].name }));
    }, [shown, byEmoji, words, query, recent]);

    function toggle(e) {
        e.stopPropagation();
        e.preventDefault();

        setIsOpen(!isOpen);
        setQuery('');
    }

    const withTone = (entry) => (tone > 0 && entry.skins ? entry.skins[tone - 1] : entry.emoji);

    function pick(entry) {
        addEmoji(withTone(entry));
        const next = [entry.emoji, ...recent.filter(emoji => emoji !== entry.emoji)].slice(0, MAX_RECENT);
        setRecent(next);
        writePreference('recentEmojis', next);
    }

    function pickTone(index) {
        setTone(index);
        writePreference('emojiSkinTone', index);
    }

    const renderEmoji = (entry) => (
        <button
            type="button"
            className="emoji-btn"
            key={entry.emoji}
            onMouseDown={(e) => { e.preventDefault(); pick(entry); }}
            onMouseEnter={() => setPreview(entry)}
        >{withTone(entry)}</button>
    );

    const scrollTo = (key) => {
        const section = listRef.current?.querySelector(`[data-group="${key}"]`);
        if (section) listRef.current.scrollTop = section.offsetTop;
        setActiveGroup(key);
    };

    const onScroll = () => {
        const list = listRef.current;
        const current = [...list.querySelectorAll('[data-group]')].filter(section => section.offsetTop <= list.scrollTop + 1).pop();
        if (current) setActiveGroup(current.dataset.group);
    };

    const onSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const first = sections[0]?.emojis[0];
            if (first) pick(first);
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            setIsOpen(false);
        }
    };

    const picker = (
        <div
            className={classNames('emoji-picker', { 'emoji-picker_above': above, 'emoji-picker_align-left': alignLeft })}
            onMouseDown={(e) => { if (e.target !== searchRef.current) e.preventDefault(); }}
        >
            <input
                ref={searchRef}
                className="emoji-search"
                type="search"
                placeholder="Поиск эмодзи"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
            />
            {!query && <div className="emoji-groups">
                {sections.map(section => (
                    <button
                        type="button"
                        key={section.key}
                        title={section.name}
                        className={classNames('emoji-groups-btn', { 'emoji-groups-btn_active': (activeGroup ?? sections[0]?.key) === section.key })}
                        onMouseDown={(e) => { e.preventDefault(); scrollTo(section.key); }}
                    >
                        {GROUPS[section.key].icon}
                    </button>
                ))}
            </div>}
            <div className="emoji-list" ref={listRef} onScroll={onScroll}>
                {!shown && <div className="emoji-empty">Загрузка…</div>}
                {sections.map((section, index) => (
                    <EmojiSection key={section.key} section={section} list={listRef} eager={index === 0} renderEmoji={renderEmoji} />
                ))}
            </div>
            <div className="emoji-footer">
                <span className="emoji-preview">{preview && withTone(preview)}</span>
                <span className="emoji-preview-name">{preview && words?.get(preview.emoji)?.name}</span>
                <span className="emoji-tones" title="Тон кожи">
                    {SKIN_TONES.map((emoji, index) => (
                        <button
                            type="button"
                            key={emoji}
                            className={classNames('emoji-tone', { 'emoji-tone_active': tone === index })}
                            onMouseDown={(e) => { e.preventDefault(); pickTone(index); }}
                        >{emoji}</button>
                    ))}
                </span>
            </div>
        </div>
    );

    return (
        <div className="emoji-select" ref={ref}>
            <button type="button" className="emoji-header" title="Эмодзи" onMouseDown={toggle}>&#128515;</button>

            <div className="emoji-select-container">
                {isOpen && picker}
            </div>
        </div>
    );
}

export default EmojiSelect;
